<script lang="ts">
  import { onMount } from 'svelte';
  import Button from '../../Button.svelte';
  import Dropdown from '../../Dropdown.svelte';
  import Input from '../../Input.svelte';

  import { MyEmployerService, type Employer } from '../../../lib/api/employee/myEmployer.service';
  import EmployeeDonationService, { type ExtendedCharity } from '../../../lib/api/employee/donation.service';
  import AdminEmployeesService from '../../../lib/api/admin/employees.service';
  import { auth } from '../../../stores/auth.store';
  import { api } from '../../../lib/api/client';
  import toast from 'svelte-french-toast';

  // Form data
  let selectedEmployee = '';
  let selectedEmployer = '';
  let selectedCharity = '';
  let amount = '';
  let selectedDate = '';

  // Options for dropdowns
  let employeeOptions: Array<{ uuid: string; name: string }> = [];
  let employerOptions: Array<{ uuid: string; name: string }> = [];
  let charityOptions: Array<{ uuid: string; name: string }> = [];

  // Loading states
  let isLoading = false;
  let isLoadingEmployees = false;
  let isLoadingEmployers = false;
  let isLoadingCharities = false;

  // Load employers for dropdown
  async function loadEmployers() {
    isLoadingEmployers = true;
    try {
      // Try to get employers using the general employer endpoint
      const response = await api.get('/employer');
      const data = response.data as any;
      
      if (data.success && data.data && Array.isArray(data.data)) {
        employerOptions = data.data.map((employer: any) => ({
          uuid: employer.uuid,
          name: employer.name
        }));
      } else {
        // Fallback: use the user-specific endpoint with a dummy UUID
        const fallbackResponse = await api.get('/user/00000000-0000-0000-0000-000000000000/employers');
        const fallbackData = fallbackResponse.data as any;
        if (fallbackData.success && fallbackData.data) {
          employerOptions = fallbackData.data.map((employer: any) => ({
            uuid: employer.uuid,
            name: employer.name
          }));
        } else {
          toast.error('Failed to load employers');
        }
      }
    } catch (err) {
      console.error('Error loading employers:', err);
      toast.error('Failed to load employers. Please try again.');
    } finally {
      isLoadingEmployers = false;
    }
  }

  // Load charities for dropdown
  async function loadCharities() {
    isLoadingCharities = true;
    try {
      const result = await EmployeeDonationService.getCharities({
        per_page: 100,
        verified: true
      });

      if (result.success && result.data) {
        charityOptions = result.data.map(charity => ({
          uuid: charity.uuid,
          name: charity.name
        }));
      } else {
        toast.error('Failed to load charities');
      }
    } catch (error) {
      console.error('Error loading charities:', error);
      toast.error('Failed to load charities. Please try again.');
    } finally {
      isLoadingCharities = false;
    }
  }

  // Load employees for dropdown
  async function loadEmployees() {
    isLoadingEmployees = true;
    try {
      const result = await AdminEmployeesService.getEmployees({
        per_page: 1000 // Get all employees
      });
      
      if (result.success && result.data) {
        employeeOptions = result.data.map(employee => ({
          uuid: employee.uuid,
          name: employee.full_name
        }));
      } else {
        toast.error('Failed to load employees');
      }
    } catch (err) {
      console.error('Error loading employees:', err);
      toast.error('Failed to load employees. Please try again.');
    } finally {
      isLoadingEmployees = false;
    }
  }

  // Handle form submission
  async function handleSubmit() {
    // Validate required fields
    if (!selectedEmployee) {
      toast.error('Please select an employee');
      return;
    }
    if (!selectedEmployer) {
      toast.error('Please select an employer');
      return;
    }
    if (!selectedCharity) {
      toast.error('Please select a charity');
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }
    if (!selectedDate) {
      toast.error('Please select a date');
      return;
    }

    // Validate date is not in the future
    const selectedDateObj = new Date(selectedDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (selectedDateObj > today) {
      toast.error('Date cannot be in the future');
      return;
    }

    try {
      isLoading = true;
      
      // Call the refund/adjust donation API endpoint
      const response = await api.post('/admin/refund-adjust-donation', {
        user_uuid: selectedEmployee,
        employer_uuid: selectedEmployer,
        charity_uuid: selectedCharity,
        amount: parseFloat(amount),
        created_at: selectedDate
      });

      const data = response.data as any;
      
      if (data.success) {
        toast.success('Refund/Adjust donation created successfully');
        // Reset form
        selectedEmployee = '';
        selectedEmployer = '';
        selectedCharity = '';
        amount = '';
        selectedDate = '';
      } else {
        toast.error(data.message || 'Failed to create refund/adjust donation');
      }
    } catch (err: any) {
      console.error('Error creating refund/adjust donation:', err);
      
      // Handle specific API error responses
      if (err.response?.status === 422) {
        const validationErrors = err.response.data?.data?.errors;
        if (validationErrors) {
          const errorMessages = Object.values(validationErrors).flat();
          toast.error(errorMessages.join(', '));
        } else {
          toast.error('Validation failed. Please check your input.');
        }
      } else if (err.response?.status === 403) {
        toast.error('You are not authorized to create refund/adjust donations');
      } else if (err.response?.status === 401) {
        toast.error('Authentication required');
      } else {
        const errorMessage = err.response?.data?.message || 'Failed to create refund/adjust donation';
        toast.error(errorMessage);
      }
    } finally {
      isLoading = false;
    }
  }

  function handleCancel() {
    // Reset form
    selectedEmployee = '';
    selectedEmployer = '';
    selectedCharity = '';
    amount = '';
    selectedDate = '';
    toast('Form cancelled');
  }

  // Reset form to initial state
  function resetForm() {
    selectedEmployee = '';
    selectedEmployer = '';
    selectedCharity = '';
    amount = '';
    selectedDate = '';
  }

  // Handle dropdown changes
  function handleEmployeeChange(value: string) {
    selectedEmployee = value;
  }

  function handleEmployerChange(value: string) {
    selectedEmployer = value;
  }

  function handleCharityChange(value: string) {
    selectedCharity = value;
  }

  function handleAmountChange(value: string) {
    amount = value;
  }

  function handleDateChange(value: string) {
    selectedDate = value;
  }

  function handleAmountInput(event: Event) {
    handleAmountChange((event.currentTarget as HTMLInputElement).value);
  }

  function handleDateInput(event: Event) {
    handleDateChange((event.currentTarget as HTMLInputElement).value);
  }

  onMount(async () => {
    // Load all dropdown options
    await Promise.all([
      loadEmployees(),
      loadEmployers(),
      loadCharities()
    ]);
  });
</script>

<div class="refund-adjust-donation">
  <div class="form-container">
    <h1 class="form-title">Refund/Adjust Donation</h1>
    
    <form class="donation-form" on:submit|preventDefault={handleSubmit}>
      <div class="form-row">
        <div class="form-group">
          <label for="employee" class="form-label">Select Employee</label>
          <Dropdown
            id="employee"
            options={employeeOptions}
            selectedValue={selectedEmployee}
            placeholder="Search or select employee..."
            onSelectionChange={handleEmployeeChange}
            disabled={isLoadingEmployees}
          />
          {#if isLoadingEmployees}
            <div class="loading-indicator">Loading employees...</div>
          {/if}
        </div>
        
        <div class="form-group">
          <label for="amount" class="form-label">Amount</label>
          <Input
            id="amount"
            type="number"
            value={amount}
            placeholder="Enter amount..."
            on:input={handleAmountInput}
            min="0"
            step="0.01"
          />
        </div>
        
        <div class="form-group">
          <label for="employer" class="form-label">Select Employer</label>
          <Dropdown
            id="employer"
            options={employerOptions}
            selectedValue={selectedEmployer}
            placeholder="Select employer..."
            onSelectionChange={handleEmployerChange}
            disabled={isLoadingEmployers}
          />
          {#if isLoadingEmployers}
            <div class="loading-indicator">Loading employers...</div>
          {/if}
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label for="charity" class="form-label">Select Charity</label>
          <Dropdown
            id="charity"
            options={charityOptions}
            selectedValue={selectedCharity}
            placeholder="Select charity..."
            onSelectionChange={handleCharityChange}
            disabled={isLoadingCharities}
          />
          {#if isLoadingCharities}
            <div class="loading-indicator">Loading charities...</div>
          {/if}
        </div>
        
        <div class="form-group">
          <label for="date" class="form-label">Date</label>
          <Input
            id="date"
            type="date"
            value={selectedDate}
            placeholder="yyyy / mm / dd"
            on:input={handleDateInput}
          />
        </div>
        
        <div class="form-group">
          <!-- Empty space for third column -->
        </div>
      </div>
    </form>
  </div>
  
  <div class="form-actions">
    <Button 
      type="submit" 
      variant="primary" 
      disabled={isLoading}
      class="submit-btn"
      on:click={handleSubmit}
    >
      {isLoading ? 'Submitting...' : 'Submit'}
    </Button>
    <Button 
      type="button" 
      variant="secondary" 
      on:click={handleCancel}
      disabled={isLoading}
      class="cancel-btn"
    >
      Cancel
    </Button>
  </div>
</div>

<style>
  .refund-adjust-donation {
    padding: 1rem;
    background-color: #f9fafb;
    min-height: 100vh;
  }

  .form-container {
    max-width: 100%;
    margin: 0;
    background: white;
    border-radius: 12px;
    padding: 2rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
    border: 1px solid #e5e7eb;
    margin-bottom: 2rem;
  }

  .form-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: #121826;
    margin: 0 0 2rem 0;
    text-align: left;
  }

  .donation-form {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .form-row {
    display: flex;
    gap: 1.5rem;
    margin-bottom: 1.5rem;
  }

  .form-group {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    min-width: 0; /* Allow flex items to shrink */
  }

  /* Override dropdown styling for consistent sizing */
  :global(.refund-adjust-donation .dropdown-container) {
    width: 100% !important;
  }
  
  :global(.refund-adjust-donation .dropdown-input) {
    height: 40px !important;
    min-height: 40px !important;
    box-sizing: border-box !important;
    padding: 0.5rem 1rem !important;
    border-radius: 0.5rem !important;
    border: 1px solid #d1d5db !important;
    font-size: 0.875rem !important;
    width: 100% !important;
  }
  
  :global(.refund-adjust-donation .input-field) {
    height: 40px !important;
    min-height: 40px !important;
    box-sizing: border-box !important;
    padding: 0.5rem 1rem !important;
    border-radius: 0.5rem !important;
    border: 1px solid #d1d5db !important;
    font-size: 0.875rem !important;
  }

  .form-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 0.25rem;
  }

  .loading-indicator {
    font-size: 0.75rem;
    color: #6b7280;
    font-style: italic;
  }

  .form-actions {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
    margin-top: 0;
    padding-top: 0;
    border-top: none;
    width: 100%;
    padding-right: 0;
    margin-right: 0;
  }

  /* Override any Button component styling */
  :global(.refund-adjust-donation .form-actions button) {
    margin: 0 !important;
    width: 267px !important;
    height: 52px !important;
    padding: 10px !important;
    border-radius: 100px !important;
    font-weight: 500 !important;
    cursor: pointer !important;
    transition: background-color 0.2s !important;
    border: none !important;
    font-size: 0.875rem !important;
    line-height: 1.25rem !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    text-decoration: none !important;
    min-height: 52px !important;
    min-width: 267px !important;
  }

  :global(.refund-adjust-donation .form-actions .submit-btn) {
    background-color: #FCB201 !important;
    color: white !important;
  }

  :global(.refund-adjust-donation .form-actions .submit-btn:hover:not(:disabled)) {
    background-color: #e6a000 !important;
  }

  :global(.refund-adjust-donation .form-actions .submit-btn:disabled) {
    background-color: #9ca3af !important;
    cursor: not-allowed !important;
  }

  :global(.refund-adjust-donation .form-actions .cancel-btn) {
    background-color: #D3D5DA !important;
    color: #374151 !important;
  }

  :global(.refund-adjust-donation .form-actions .cancel-btn:hover:not(:disabled)) {
    background-color: #c1c3c8 !important;
  }

  :global(.refund-adjust-donation .form-actions .cancel-btn:disabled) {
    background-color: #9ca3af !important;
    cursor: not-allowed !important;
  }

  /* Responsive design */
  @media (max-width: 1024px) {
    .form-row {
      flex-direction: column;
      gap: 1rem;
    }
    
    .form-group {
      min-width: auto;
    }

    /* Responsive buttons for tablet */
    :global(.refund-adjust-donation .form-actions button) {
      width: 200px !important;
      min-width: 200px !important;
    }
  }

  @media (max-width: 768px) {
    .refund-adjust-donation {
      padding: 1rem;
    }

    .form-container {
      padding: 1.5rem;
      max-width: 100%;
    }

    .form-actions {
      flex-direction: column;
      gap: 1rem;
    }

    /* Responsive buttons for mobile */
    :global(.refund-adjust-donation .form-actions button) {
      width: 100% !important;
      min-width: 100% !important;
      height: 48px !important;
      min-height: 48px !important;
    }
  }

  @media (max-width: 480px) {
    .form-container {
      padding: 1rem;
    }

    /* Even smaller buttons for very small screens */
    :global(.refund-adjust-donation .form-actions button) {
      height: 44px !important;
      min-height: 44px !important;
      font-size: 0.875rem !important;
    }
  }
</style> 